
import { trackEvent } from '@@/bits/analytics'
import window from 'bits/global'
import { buildUrlFromPath, goBack, reload, transformUrl } from 'bits/location'
import { Action, Component, Getter, State, Vue, Watch, namespace } from 'bits/vue'
import OzBackButton from 'library/v4/components/OzBackButton.vue'
import OzCheckBox from 'library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzInput from 'library/v4/components/OzInput.vue'
import OzLabelInput from 'library/v4/components/OzLabelInput.vue'
import OzLoadingText from 'library/v4/components/OzLoadingText.vue'
import OzPadletLogo from 'library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
OzPlainButtonColorScheme,
OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import OzTextLink from 'library/v4/components/OzTextLink.vue'
import AuthActionMenu from 'vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from 'vuecomponents/AuthBottomNav.vue'
import AuthLoginThirdPartyGroupButtons, {
AuthLoginThirdPartyGroupButtonsType,
} from 'vuecomponents/AuthLoginThirdPartyGroupButtons.vue'
import GlobalAlertDialog from 'vuecomponents/GlobalAlertDialogVuex.vue'
import GlobalConfirmationDialog from 'vuecomponents/GlobalConfirmationDialogVuex.vue'
import GlobalSnackbar from 'vuecomponents/GlobalSnackbarVuex.vue'
import VisualViewportSizeMixin from 'vuemixins/visual_viewport_size_mixin'
import WindowSizeMixin from 'vuemixins/window_size_mixin'
import type { LoginMethod } from 'vuexstore/landside'

const SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY = 'signup_flow_email_verification_experiment'

const browserStore = namespace('browser')

// New Auth Signup Flow Screens
enum AuthSignupScreen {
  EmailValidation,
  EmailVerificationWithOtp,
  PasswordSubmission,
}

enum AuthSignupUserAction {
  SubmitEmail = 'submit_email',
  SubmitPassword = 'submit_password',
  SubmitVerificationCode = 'submit_verification_code',
  ResendVerificationCode = 'resend_verification_code',
}

enum EmailVerificationExperiment {
  Enabled = 'email_verification_enabled',
  Disabled = 'email_verification_not_enabled',
}

@Component({
  components: {
    OzIcon,
    OzContainedButton,
    OzBackButton,
    OzLabelInput,
    OzInput,
    OzCheckBox,
    OzTextLink,
    OzLoadingText,
    OzPadletLogo,
    OzPlainButton,
    AuthLoginThirdPartyGroupButtons,
    AuthActionMenu,
    AuthBottomNav,
    GlobalAlertDialog,
    GlobalConfirmationDialog,
    GlobalSnackbar,
  },
  mixins: [WindowSizeMixin, VisualViewportSizeMixin],
})
export default class AuthSignupApp extends Vue {
  $refs!: {
    emailInput: any
    verificationCodeInput: any
    passwordInput: any
  }

  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset
  OzContainedButtonColorScheme = OzContainedButtonColorScheme
  AuthLoginThirdPartyGroupButtonsType = AuthLoginThirdPartyGroupButtonsType

  xActionMenu = false
  isEmailInputFocused = false
  isPasswordInputFocused = false
  isVerificationCodeInputFocused = false
  isPasswordShown = false
  initialScrollY = 0

  AuthSignupScreen = AuthSignupScreen

  @State email!: string
  @State isEmailVerified!: boolean
  @State isEmailValidatedForSignup!: boolean
  @State isEmailVerifiedWithOtp!: boolean
  @State enforceEmailVerification!: boolean
  @State verificationCode!: string
  @State password!: string
  @State emailValidationMessage!: string
  @State verificationCodeValidationMessage!: string
  @State passwordValidationMessage!: string
  @State isAwaitingServerResponse!: boolean
  @State minimumPasswordLength!: number
  @State dir!: string
  @State isElectronApp!: boolean
  // State variables for PKCE verification
  @State codeChallenge!: string | null
  @State codeChallengeMethod!: string | null
  @State pkceState!: string | null
  @State originAppClientId!: string | null
  @State originAppRedirectUri!: string | null
  @Getter isPkceEnabled!: boolean
  @Getter isEmailValid!: boolean
  @Getter isVerificationCodeValid!: boolean
  @Getter isPasswordValid!: boolean
  @Getter thirdPartyAuthLoginMethods!: Record<string, LoginMethod>
  @Getter thirdPartyAuthVerifyMethods!: Record<string, LoginMethod>
  @Action setEmail!: (email: string) => void
  @Action setVerificationCode!: (verificationCode: string) => void
  @Action setPassword!: (password: string) => void
  @Action setEmailValidationMessage!: (message: string) => void
  @Action setVerificationCodeValidationMessage!: (message: string) => void
  @Action setPasswordValidationMessage!: (message: string) => void
  @Action setIsEmailVerified!: (value: boolean) => void
  @Action setIsEmailValidatedForSignup!: (value: boolean) => void
  @Action setIsEmailVerifiedWithOtp!: (value: boolean) => void
  @Action continueSignupWithEmail!: () => Promise<void>
  @Action continueWithOtpVerification!: () => Promise<void>
  @Action completeSignupWithPassword!: () => void
  @Action resendVerificationCode!: () => Promise<void>
  @browserStore.Getter visualViewportHeight!: number
  @browserStore.Getter isSmallerThanTabletPortrait!: boolean
  @browserStore.Getter isMobile!: boolean
  @browserStore.Getter isTablet!: boolean
  @browserStore.Getter isDesktop!: boolean
  @browserStore.Getter isAndroid!: boolean

  // Header elements are hidden when the input is focused on android phones.
  get xHeader(): boolean {
    return !(
      this.isMobile &&
      this.isAndroid &&
      this.isSmallerThanTabletPortrait &&
      (this.isEmailInputFocused || this.isPasswordInputFocused)
    )
  }

  get isVerifyButtonDisabled(): boolean {
    return !this.verificationCode.length || !!this.verificationCodeValidationMessage.length
  }

  get isContinueButtonDisabled(): boolean {
    return !this.email.length || !!this.emailValidationMessage.length
  }

  get isCreateAccountButtonDisabled(): boolean {
    return !this.password.length || !!this.passwordValidationMessage.length
  }

  get loginPageUrl(): string {
    const baseLoginUrl = buildUrlFromPath('/auth/login')
    if (this.isPkceEnabled) {
      const loginUrlWithPkceParams = transformUrl(baseLoginUrl, {
        searchParams: {
          code_challenge: this.codeChallenge as string,
          code_challenge_method: this.codeChallengeMethod as string,
          redirect_uri: encodeURIComponent(this.originAppRedirectUri as string),
          client_id: this.originAppClientId as string,
          pkce_state: this.pkceState as string,
        },
      })
      return loginUrlWithPkceParams
    } else {
      return baseLoginUrl
    }
  }

  get xEmailVerificationScreen(): boolean {
    return !this.isEmailVerified
  }

  get isEmailVerificationEnabled(): boolean {
    return this.enforceEmailVerification
  }

  get currentScreen(): AuthSignupScreen | undefined {
    if (!this.isEmailVerificationEnabled) {
      return undefined
    }
    if (!this.isEmailValidatedForSignup) {
      return AuthSignupScreen.EmailValidation
    }
    if (this.isEmailValidatedForSignup && !this.isEmailVerifiedWithOtp) {
      return AuthSignupScreen.EmailVerificationWithOtp
    }
    if (this.isEmailVerified && this.isEmailVerifiedWithOtp) {
      return AuthSignupScreen.PasswordSubmission
    }
    return AuthSignupScreen.EmailValidation
  }

  get termOfServiceAndPrivacyPolicyText(): string {
    const classes = 'oz-text-link-light dark:oz-text-link-dark'
    return this.__(
      'By signing up, you agree to our %{termsOfService} and %{privacyPolicy}. You also admit that you are beautiful.',
      {
        termsOfService: `<a href="https://legal.padlet.com/terms" target="_blank" class="${classes}">${this.__(
          'Terms of Service',
        )}</a>`,
        privacyPolicy: `<a href="https://legal.padlet.com/privacy" target="_blank" class="${classes}">${this.__(
          'Privacy Policy',
        )}</a>`,
      },
    )
  }

  get resendCodeText(): string {
    return this.__(
      'Didn’t get the email? <button style="vertical-align: baseline" class="font-sans bg-transparent border-none select-none oz-text-link-light dark:oz-text-link-dark font-semibold">Resend code</button>',
    )
  }

  @Watch('visualViewportHeight')
  respondToVisualViewportHeightChange(newValue: number, oldValue: number): void {
    if (this.isDesktop || newValue >= oldValue) return

    this.initialScrollY = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (this.isEmailInputFocused) {
      document.addEventListener('scroll', this.blurEmailInputOnScroll)
    } else if (this.isPasswordInputFocused) {
      document.addEventListener('scroll', this.blurPasswordInputOnScroll)
    }
  }

  @Watch('xEmailVerificationScreen', { immediate: true })
  respondToScreenChange(newValue: boolean): void {
    // Only focus on current screen input field after a 1ms `setTimeout`,
    // because we need to wait for the input field to be shown in the DOM,
    // before we can focus on it.
    setTimeout(() => {
      this.$nextTick(() => {
        if (newValue) {
          this.$refs.emailInput.focusInput()
        } else {
          this.$refs.passwordInput.focusInput()
        }
      })
    }, 1)
  }

  @Watch('isAwaitingServerResponse')
  handleIsAwaitingServerResponse(value: boolean): void {
    const html = document.getElementsByTagName('html')[0]

    if (this.xEmailVerificationScreen) return

    // Animate the background color while waiting for the response
    // Note: Only do this when submitting the signup form
    if (value) {
      html.classList.add('dot-grid-loading')
    } else {
      html.classList.remove('dot-grid-loading')
    }
  }

  @Watch('currentScreen', { immediate: true })
  handleCurrentScreenChange(value: AuthSignupScreen | undefined): void {
    setTimeout(() => {
      this.$nextTick(() => {
        if (value === AuthSignupScreen.EmailValidation) {
          this.$refs.emailInput.focusInput()
        } else if (value === AuthSignupScreen.EmailVerificationWithOtp) {
          this.$refs.verificationCodeInput.focusInput()
        } else if (value === AuthSignupScreen.PasswordSubmission) {
          this.$refs.passwordInput.focusInput()
        }
      })
    }, 1)
  }

  beforeDestroy(): void {
    window.removeEventListener('popstate', this.goBackEmailVerificationScreen)
    window.removeEventListener('popstate', this.goBackToPreviousScreen)
  }

  onInputEmailInput(value: string): void {
    this.setEmail(value)

    // Reset the email validation message to enable the Continue button when typing on mobile and tablet
    if (this.emailValidationMessage.length) this.setEmailValidationMessage('')
  }

  onInputVerificationCodeInput(value: string): void {
    this.setVerificationCode(value)

    // Reset the verification code validation message to enable the Continue button when typing on mobile and tablet
    if (this.verificationCodeValidationMessage.length) this.setVerificationCodeValidationMessage('')
  }

  onInputPasswordInput(value: string): void {
    this.setPassword(value)

    // Reset the password validation message to enable the Login button when typing on mobile and tablet
    if (this.passwordValidationMessage.length) this.setPasswordValidationMessage('')
  }

  onFocusInEmailInput(): void {
    this.isEmailInputFocused = true
  }

  onFocusInVerificationCodeInput(): void {
    this.isVerificationCodeInputFocused = true
  }

  onFocusInPasswordInput(): void {
    this.isPasswordInputFocused = true
  }

  onBlurEmailInput(): void {
    this.isEmailInputFocused = false
    document.removeEventListener('scroll', this.blurEmailInputOnScroll)
  }

  onBlurVerificationCodeInput(): void {
    this.isVerificationCodeInputFocused = false
    document.removeEventListener('scroll', this.blurVerificationCodeInputOnScroll)
  }

  onBlurPasswordInput(): void {
    this.isPasswordInputFocused = false
    document.removeEventListener('scroll', this.blurPasswordInputOnScroll)
  }

  blurEmailInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.emailInput.blurInput()
    }
  }

  blurVerificationCodeInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.verificationCodeInput.blurInput()
    }
  }

  blurPasswordInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.passwordInput.blurInput()
    }
  }

  isExceededScrollableThreshold(currentWindowY: number): boolean {
    const SCROLLABLE_THRESHOLD = 40 // the height of the input field
    return (
      currentWindowY > this.initialScrollY + SCROLLABLE_THRESHOLD ||
      currentWindowY < this.initialScrollY - SCROLLABLE_THRESHOLD
    )
  }

  goBackEmailVerificationScreen(event: PopStateEvent | Event): void {
    if (event.type === 'click') {
      goBack()
    }

    this.isPasswordShown = false
    this.setIsEmailVerified(false)
    this.setPassword('')
    this.setPasswordValidationMessage('')
  }

  goBackToPreviousScreen(event: PopStateEvent | Event): void {
    if (this.currentScreen === AuthSignupScreen.PasswordSubmission) {
      this.isPasswordShown = false
      this.setIsEmailVerifiedWithOtp(false)
      this.setPassword('')
      this.setPasswordValidationMessage('')
    } else if (this.currentScreen === AuthSignupScreen.EmailVerificationWithOtp) {
      this.setVerificationCode('')
      this.setVerificationCodeValidationMessage('')
      this.setIsEmailValidatedForSignup(false)
    }
  }

  async handleSubmitSignupForm(): Promise<void> {
    if (this.isEmailVerificationEnabled) {
      await this.submitEmailOtpVerificationEnabled()
    } else {
      await this.submit()
    }
  }

  async submitEmailOtpVerificationEnabled(): Promise<void> {
    if (this.currentScreen === AuthSignupScreen.EmailValidation) {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.SubmitEmail,
        EmailVerificationExperiment.Enabled,
      )
      await this.continueSignupWithEmail()
      if (this.isEmailValidatedForSignup) {
        // Enable the browser back button on signup page
        window.history.pushState({}, '', window.location.href)
      }

      window.addEventListener('popstate', this.goBackToPreviousScreen)
    } else if (this.currentScreen === AuthSignupScreen.EmailVerificationWithOtp) {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.SubmitVerificationCode,
        EmailVerificationExperiment.Enabled,
      )
      await this.continueWithOtpVerification()
      if (this.isEmailVerifiedWithOtp) {
        // Enable the browser back button on signup page
        window.history.pushState({}, '', window.location.href)
      }
      window.addEventListener('popstate', this.goBackToPreviousScreen)
    } else {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.SubmitPassword,
        EmailVerificationExperiment.Enabled,
      )
      this.completeSignupWithPassword()
    }
  }

  async submit(): Promise<void> {
    if (this.xEmailVerificationScreen) {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.SubmitEmail,
        EmailVerificationExperiment.Disabled,
      )
      await this.continueSignupWithEmail()

      if (this.isEmailVerified && !window.history.state?.didPushState) {
        // Enable the browser back button on signup page
        // didPushState: true to prevent pushing redundant states
        window.history.pushState({ didPushState: true }, '', window.location.href)
      }

      window.addEventListener('popstate', this.goBackEmailVerificationScreen)
    } else {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.SubmitPassword,
        EmailVerificationExperiment.Disabled,
      )
      this.completeSignupWithPassword()
    }
  }

  async resendCode(): Promise<void> {
    if (!this.isAwaitingServerResponse) {
      trackEvent(
        SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
        AuthSignupUserAction.ResendVerificationCode,
        EmailVerificationExperiment.Enabled,
      )
      await this.resendVerificationCode()
    }
  }
}
